import { useLocation, useSearchParams } from 'react-router-dom';

import { AdminHeader } from '../../../../components/ui/AdminNavigation';
import { ModuleSidebar, type ModuleSidebarGroup } from '../../../../components/ui/ModuleSidebar';
import { PageBreadcrumb, PageContentCard } from '../../../../components/ui/ModulePage';
import { DataImportPanel, type DataImportPanelProps } from './data-import/DataImportPanel';
import { adminGroups, adminOverview, findAdminItem } from './navigation';
import logoSrc from './assets/logo.png';
import assistantSrc from './assets/search-assistant.png';
import avatarSrc from '../../../../assets/teacher-avatars/teacher-2.png';
import badgeSrc from './assets/enterprise.png';
import styles from './Page.module.css';

/*
 * 数据导入下各二级菜单的内容配置：目前只有「订单导入」接了完整功能页，
 * 其余仍是占位，接入时在这里补一条即可。
 */
const dataImportViews: Record<string, DataImportPanelProps> = {
  'order-import': {
    modulePath: '业务系统-前台业务-订单管理',
    templatePrefix: '订单导入模板',
  },
};

export function AdminV1Page() {
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();
  const active = findAdminItem(searchParams.get('page'));
  const requestedSub = searchParams.get('view');
  const activeSub = active?.item.sections
    ?.flatMap((section) => section.items)
    .find((item) => item.id === requestedSub);

  /* 第一列：概览入口 + 分组菜单，都按 ModuleSidebar 的后台外观渲染。 */
  const mainGroups: readonly ModuleSidebarGroup[] = [
    { href: `#${pathname}`, icon: adminOverview.icon, id: adminOverview.id, title: adminOverview.label },
    ...adminGroups.map((group): ModuleSidebarGroup => ({
      icon: group.icon,
      id: group.id,
      items: group.items.map((item) => ({ href: `#${pathname}?page=${item.id}`, label: item.label })),
      title: group.label,
    })),
  ];

  /* 第二列：当前功能自己的二级菜单，只有数据导入这类功能才有。 */
  const subGroups: readonly ModuleSidebarGroup[] = (active?.item.sections ?? []).map((section): ModuleSidebarGroup => ({
    id: section.id,
    items: section.items.map((item) => ({
      href: `#${pathname}?page=${active?.item.id}&view=${item.id}`,
      label: item.label,
    })),
    title: section.title,
  }));

  const currentLabel = activeSub?.label ?? active?.item.label ?? adminOverview.label;
  const dataImportView = activeSub ? dataImportViews[activeSub.id] : undefined;
  /* 参考实现的面包屑是「分组 / 功能 /（二级菜单）」：没有二级菜单时功能名就是当前项，不重复。 */
  const breadcrumbItems = active
    ? activeSub
      ? [active.group.label, active.item.label]
      : [active.group.label]
    : [];

  return <div className={styles.page}>
    <AdminHeader assistantSrc={assistantSrc} avatarSrc={avatarSrc} badgeSrc={badgeSrc} logoSrc={logoSrc} />
    <div className={styles.workspace}>
      <ModuleSidebar
        activeItem={active?.item.label ?? adminOverview.label}
        appearance="admin"
        ariaLabel="后台导航"
        groups={mainGroups}
      />
      <section aria-label="管理后台内容" className={styles.content}>
        <PageBreadcrumb current={currentLabel} items={breadcrumbItems} />
        <PageContentCard className={styles.card}>
          <div className={styles.cardBody}>
            {active?.item.sections ? (
              <ModuleSidebar
                activeItem={activeSub?.label ?? ''}
                appearance="admin"
                ariaLabel={`${active.item.label}菜单`}
                caret="solid"
                defaultExpandedGroups="all"
                groups={subGroups}
                showCollapseControl={false}
                surface
                title={active.item.label}
              />
            ) : null}
            {dataImportView ? <DataImportPanel {...dataImportView} /> : <div className={styles.panel}>
              <h1 className={styles.heading}>{currentLabel === adminOverview.label ? '欢迎来到管理后台' : currentLabel}</h1>
              <p className={styles.placeholder}>
                {currentLabel === adminOverview.label ? '请从左侧选择要配置的功能' : '该功能页面建设中'}
              </p>
            </div>}
          </div>
        </PageContentCard>
      </section>
    </div>
  </div>;
}
